/**
 * Leitura de série de N períodos na camada de exibição.
 *
 * Espelha `dashboard/backend/services/lote.js` e `services/projecao.js`. São
 * duas implementações porque o CRA não importa de fora de `src/`; se uma regra
 * mudar, a outra muda junto.
 *
 * DUAS REGRAS QUE MANDAM AQUI
 *
 * 1. **Lacuna é `null`, nunca zero.** Zero é medição: um mês com R$ 0,00 de
 *    faturamento é uma informação, um mês não informado é outra. A tela mostra
 *    `—` para o segundo, e o gráfico não liga os pontos por cima dele.
 *
 * 2. **A quantidade de períodos vem do dado.** Nada aqui assume três colunas:
 *    a janela de exibição é a que o payload trouxer, de 1 a N meses.
 */

/** O que a tela mostra onde não há medição. */
export const TRACO = '—';

/**
 * Leitor tolerante de folha de conta — PERMANENTE, não é shim de migração.
 *
 * Todo snapshot gravado até hoje guarda `{mes1, ..., mesN}`; a projeção de N
 * períodos guarda `{valores: []}`. Ler os dois aqui é o que faz snapshot antigo
 * continuar renderizando sem conversão de banco.
 */
export const valoresDaConta = (conta, quantidade = null) => {
  let base = [];
  if (Array.isArray(conta)) {
    base = conta;
  } else if (conta && Array.isArray(conta.valores)) {
    base = conta.valores;
  } else if (conta && typeof conta === 'object') {
    // As chaves `mesN` que a conta REALMENTE tem, em ordem numérica.
    //
    // A lista era fixa em três. Quando o questionário passou a coletar 6 e 12
    // meses, a conta-folha continuou saindo do `manualEntryTransform` como
    // `{mes1..mes12}` e esta função devolvia só os três primeiros: a tabela do
    // DRE mostrava um ano de empresa com um trimestre de valores, sem nada
    // acusar. Ordenar por número importa porque `mes10` vem antes de `mes2` na
    // ordem alfabética que o `sort` usa por padrão.
    base = Object.keys(conta)
      .filter((campo) => /^mes\d+$/.test(campo))
      .sort((a, b) => Number(a.slice(3)) - Number(b.slice(3)))
      .map((campo) => conta[campo]);
  }

  const serie = base.map(numeroOuNulo);
  return quantidade === null ? serie : alinhar(serie, quantidade);
};

/**
 * Converte para número preservando os três estados.
 *
 * NUNCA usar `Number(x) || 0` em valor que veio do usuário: ele transforma
 * célula em branco em zero e apaga a diferença entre "não informado" e "medi e
 * deu zero".
 */
export const numeroOuNulo = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
};

/** Série do tamanho da janela: sobra é cortada, falta vira lacuna. */
export const alinhar = (serie, quantidade) =>
  Array.from({ length: quantidade }, (_, i) => numeroOuNulo((serie || [])[i]));

/** Série toda em lacuna, do tamanho da janela. */
export const vazia = (quantidade) => Array.from({ length: quantidade }, () => null);

/**
 * Quantos períodos o DRE tem.
 *
 * Mesma cadeia de `lote.quantidadeDeMeses`: a série de faturamento manda, e os
 * períodos rotulados são o segundo recurso. Sem nenhum dos dois não há janela.
 */
export const quantidadeDePeriodos = (dre) => {
  const serie = dre?.indicadores?.faturamento?.valores;
  if (Array.isArray(serie) && serie.length) return serie.length;
  if (Array.isArray(dre?.periodos) && dre.periodos.length) return dre.periodos.length;
  return 0;
};

/** Índice do período mais recente que tem medição. `-1` quando não há nenhuma. */
export const ultimoIndiceComDado = (serie) => {
  if (!Array.isArray(serie)) return -1;
  for (let i = serie.length - 1; i >= 0; i -= 1) {
    if (serie[i] !== null && serie[i] !== undefined) return i;
  }
  return -1;
};

/** Último valor conhecido da série. `null` quando a série inteira é lacuna. */
export const ultimoValor = (serie) => {
  const i = ultimoIndiceComDado(serie);
  return i === -1 ? null : serie[i];
};

/** Soma ignorando lacuna, mas devolvendo `null` quando NADA foi informado. */
export const somarConhecidos = (valores) => {
  const conhecidos = (valores || []).filter((v) => v !== null && v !== undefined);
  return conhecidos.length ? conhecidos.reduce((s, v) => s + v, 0) : null;
};

/** `true` quando algum período traz medição diferente de zero. */
export const temMedicao = (valores) =>
  (valores || []).some((v) => v !== null && v !== undefined && Math.abs(v) > 0.001);

/** Operação entre séries que propaga lacuna: faltou um lado, não há resultado. */
export const combinar = (a, b, operacao) =>
  (a || []).map((valor, i) => {
    const outro = (b || [])[i];
    if (valor === null || valor === undefined) return null;
    if (outro === null || outro === undefined) return null;
    return operacao(valor, outro);
  });

/** Multiplica a série inteira por um escalar, preservando lacuna. */
export const escalar = (serie, fator) =>
  (serie || []).map((v) => (v === null || v === undefined ? null : v * fator));

const MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** `null` vira `—`. Exibir `R$ 0,00` para dado ausente é afirmar uma medição. */
export const formatarMoeda = (valor) =>
  valor === null || valor === undefined || Number.isNaN(valor) ? TRACO : MOEDA.format(valor);

/**
 * Percentual e decimal em pt-BR — vírgula, como a moeda ao lado.
 *
 * `toFixed` imprime com ponto, então a mesma dobra exibia "R$ 9.689,40" e
 * "33.2%" lado a lado. Num produto financeiro para PME brasileira, número com
 * separador trocado lê como sistema mal-acabado, e a dúvida contamina o número.
 */
const decimal = (casas) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });

export const formatarPercentual = (valor, casas = 2) =>
  valor === null || valor === undefined || Number.isNaN(valor)
    ? TRACO
    : `${decimal(casas).format(valor)}%`;

export const formatarDecimal = (valor, casas = 2) =>
  valor === null || valor === undefined || Number.isNaN(valor)
    ? TRACO
    : decimal(casas).format(valor);

/**
 * Cobertura da janela exibida: quantos meses foram informados de quantos.
 *
 * A projeção emite `indicadores._cobertura` pronta. Quando o payload não traz
 * (snapshot legado, que é sempre contíguo por construção), ela é derivada da
 * própria série de faturamento — que é o que define se o mês existe.
 */
export const coberturaDoDRE = (dre) => {
  const pronta = dre?.indicadores?._cobertura;
  if (pronta && typeof pronta.total === 'number') return pronta;

  const janela = quantidadeDePeriodos(dre);
  if (!janela) return null;

  const faturamento = dre?.indicadores?.faturamento?.valores || [];
  const informados = Array.from({ length: janela }, (_, i) => faturamento[i])
    .filter((v) => v !== null && v !== undefined).length;

  return { total: informados, janela, contiguoMax: informados, lacunas: [], legado: true };
};
